import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditDeviceProfileComposer } from "@/forms/sensory"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Layers, Loader2 } from "lucide-react"

function EditDeviceProfilePage() {
  const { deviceProfileNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery("getDeviceProfile", { deviceProfileNumber })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = (data as any)?.content?.deviceProfile

  const handleBack = () => navigate({ to: '/sensory/device-profiles' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Layers className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Device Profile</CardTitle>
              <CardDescription className="text-base">Update device profile details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {p ? (
            <EditDeviceProfileComposer
              deviceProfileNumber={p.deviceProfileNumber}
              initialValues={{
                deviceProfileName: p.deviceProfileName,
                deviceProfileNetwork: p.deviceProfileNetwork,
                // Backend field pending (Feature 7 hand-off); default to "yes" until it returns a value.
                provisioningRequired: (p as { provisioningRequired?: boolean }).provisioningRequired === false ? 'no' : 'yes',
                region: p.region,
                macVersion: p.macVersion,
                supportsOtaa: p.supportsOtaa,
                supportsClassB: p.supportsClassB,
              }}
              submitLabel="Save Profile"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update device profile: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Device profile not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/sensory/device-profiles/$deviceProfileNumber_/edit")({
  staticData: {
    title: 'Edit Device Profile',
    isSecured: true,
  },
  component: EditDeviceProfilePage,
})
